import type { ReactNode, RefObject } from 'react';
import { LockOutlined } from '@mui/icons-material';
import { Box, Button, Stack, Typography } from '@mui/material';
import { CompactEditButton, designTokens, FormTextField, HelpPopover } from '../../../design-system';
import {
  hireDateError,
  legalFullName,
  minimumHireDate,
  nameError,
  type InitialAdminSetupInput,
  type InitialAdminSetupSession,
  type InputStepId,
  type StepId,
} from '../model/setup';

type ActiveStepPanelProps = {
  activeStep: StepId;
  draft: InitialAdminSetupInput;
  setup: InitialAdminSetupSession;
  attemptedSteps: Set<InputStepId>;
  today: string;
  saving: boolean;
  allComplete: boolean;
  headingRef: RefObject<HTMLHeadingElement | null>;
  onSelectStep: (step: StepId) => void;
  onUpdateField: (step: InputStepId, field: keyof InitialAdminSetupInput, value: string) => void;
  onConfirmStep: (step: InputStepId) => void;
  onSave: () => void;
};

export function ActiveStepPanel({ activeStep, draft, setup, attemptedSteps, today, saving, allComplete, headingRef, onSelectStep, onUpdateField, onConfirmStep, onSave }: ActiveStepPanelProps) {
  if (activeStep === 'account') {
    return (
      <PanelFrame headingRef={headingRef} step="1 / 6" title="Google 계정" description="로그인에 사용한 Google Workspace 계정입니다.">
        <Box component="dl" sx={{ m: 0, borderTop: `1px solid ${designTokens.color.divider}` }}>
          <VerifiedRow label="프로필 이름" value={setup.displayName || '이름 정보 없음'} />
          <VerifiedRow label="이메일" value={setup.email} />
        </Box>
        <Button variant="contained" onClick={() => onSelectStep('company')} sx={{ alignSelf: 'flex-start' }}>다음</Button>
      </PanelFrame>
    );
  }

  if (activeStep === 'review') {
    return (
      <PanelFrame headingRef={headingRef} step="6 / 6" title="입력 내용 확인" description="아래 정보로 조직과 첫 관리자 계정을 생성합니다.">
        <Stack component="form" spacing={2.5} onSubmit={(event) => { event.preventDefault(); onSave(); }}>
          <Box sx={{ borderTop: `1px solid ${designTokens.color.divider}` }}>
            <ReviewItem label="회사" value={draft.companyName} meta="참조 코드 자동 발급" onEdit={() => onSelectStep('company')} />
            <ReviewItem label="최상위 조직" value={draft.rootOrganizationName} meta="참조 코드 자동 발급" onEdit={() => onSelectStep('organization')} />
            <ReviewItem label="기본 근무지" value={draft.locationName} meta="참조 코드 자동 발급" onEdit={() => onSelectStep('location')} />
            <ReviewItem label="첫 관리자" value={legalFullName(draft)} meta={`${draft.hireDate} · ${setup.email}`} onEdit={() => onSelectStep('person')} />
          </Box>
          <Stack direction="row" spacing={0.75} sx={{ color: designTokens.color.secondaryText, alignItems: 'flex-start' }}>
            <LockOutlined sx={{ fontSize: 15, mt: '1px' }} />
            <Typography variant="caption" sx={{ lineHeight: 1.55 }}>생성하면 이 설정 권한은 즉시 종료되며, 이후에는 일반 Google 로그인만 사용할 수 있습니다.</Typography>
          </Stack>
          <Button type="submit" variant="contained" disabled={!allComplete || saving} sx={{ alignSelf: 'flex-start', minWidth: 190 }}>
            {saving ? '생성 중…' : '조직 및 관리자 생성하기'}
          </Button>
        </Stack>
      </PanelFrame>
    );
  }

  const attempted = attemptedSteps.has(activeStep);
  if (activeStep === 'company') {
    return (
      <InputStepForm headingRef={headingRef} step="company" stepLabel="2 / 6" title="회사 정보" description="회사명은 Heyday에 표시됩니다. 참조 코드는 생성할 때 XXXX-XXXX 형식으로 자동 발급됩니다." saving={saving} onConfirm={onConfirmStep}>
        <NameField wide label="회사명" placeholder="예: 주식회사 한결" value={draft.companyName} attempted={attempted} onChange={(value) => onUpdateField('company', 'companyName', value)} />
      </InputStepForm>
    );
  }
  if (activeStep === 'organization') {
    return (
      <InputStepForm headingRef={headingRef} step="organization" stepLabel="3 / 6" title="최상위 조직" description="조직도에서 가장 위에 표시할 조직입니다. 참조 코드는 생성할 때 XXXX-XXXX 형식으로 자동 발급됩니다." saving={saving} onConfirm={onConfirmStep}>
        <NameField wide label="최상위 조직명" placeholder="예: 한결 전사" value={draft.rootOrganizationName} attempted={attempted} onChange={(value) => onUpdateField('organization', 'rootOrganizationName', value)} />
      </InputStepForm>
    );
  }
  if (activeStep === 'location') {
    return (
      <InputStepForm headingRef={headingRef} step="location" stepLabel="4 / 6" title="기본 근무지" description="첫 관리자에게 연결할 근무지입니다. 참조 코드는 생성할 때 XXXX-XXXX 형식으로 자동 발급됩니다." saving={saving} onConfirm={onConfirmStep}>
        <NameField wide label="근무지명" placeholder="예: 서울 본사" value={draft.locationName} attempted={attempted} onChange={(value) => onUpdateField('location', 'locationName', value)} />
      </InputStepForm>
    );
  }
  return (
    <InputStepForm headingRef={headingRef} step="person" stepLabel="5 / 6" title="본인 정보" description="인사 기록에 사용할 법적 이름과 입사일을 입력합니다." saving={saving} submitLabel="완료하고 검토" onConfirm={onConfirmStep}>
      <NameField label="법적 성" placeholder="예: 이" value={draft.legalFamilyName} maxLength={100} attempted={attempted} onChange={(value) => onUpdateField('person', 'legalFamilyName', value)} />
      <NameField label="법적 이름" placeholder="예: 정행" value={draft.legalGivenName} maxLength={100} attempted={attempted} onChange={(value) => onUpdateField('person', 'legalGivenName', value)} />
      <FormTextField
        required fullWidth type="date" label="입사일" value={draft.hireDate}
        onChange={(event) => onUpdateField('person', 'hireDate', event.target.value)}
        error={attempted && Boolean(hireDateError(draft.hireDate, today))}
        helperText={attempted ? hireDateError(draft.hireDate, today) : undefined}
        slotProps={{ htmlInput: { min: minimumHireDate, max: today } }}
        sx={{ gridColumn: { sm: '1 / -1' } }}
      />
    </InputStepForm>
  );
}

function PanelFrame({ headingRef, step, title, description, children }: { headingRef: RefObject<HTMLHeadingElement | null>; step: string; title: string; description: string; children: ReactNode }) {
  return (
    <Stack spacing={3} sx={{ width: '100%', maxWidth: 680 }}>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>{step}</Typography>
        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, alignItems: 'center' }}>
          <Typography ref={headingRef} tabIndex={-1} variant="h5" component="h2" sx={{ outline: 'none' }}>{title}</Typography>
          <HelpPopover title={title}>{description}</HelpPopover>
        </Stack>
      </Box>
      {children}
    </Stack>
  );
}

function InputStepForm({ headingRef, step, stepLabel, title, description, saving, submitLabel = '완료하고 다음', onConfirm, children }: { headingRef: RefObject<HTMLHeadingElement | null>; step: InputStepId; stepLabel: string; title: string; description: string; saving: boolean; submitLabel?: string; onConfirm: (step: InputStepId) => void; children: ReactNode }) {
  return (
    <PanelFrame headingRef={headingRef} step={stepLabel} title={title} description={description}>
      <Box component="form" noValidate onSubmit={(event) => { event.preventDefault(); onConfirm(step); }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
          {children}
        </Box>
        <Button type="submit" variant="contained" disabled={saving} sx={{ mt: 2.5 }}>{submitLabel}</Button>
      </Box>
    </PanelFrame>
  );
}

function NameField({ label, placeholder, value, maxLength = 200, attempted, wide = false, onChange }: { label: string; placeholder: string; value: string; maxLength?: number; attempted: boolean; wide?: boolean; onChange: (value: string) => void }) {
  const errorMessage = nameError(label, value, maxLength);
  return (
    <FormTextField
      required fullWidth label={label} placeholder={placeholder} value={value}
      onChange={(event) => onChange(event.target.value)}
      error={attempted && Boolean(errorMessage)}
      helperText={attempted ? errorMessage : undefined}
      slotProps={{ htmlInput: { maxLength } }}
      sx={wide ? { gridColumn: { sm: '1 / -1' } } : undefined}
    />
  );
}

function VerifiedRow({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '140px minmax(0, 1fr)' }, gap: { xs: 0.4, sm: 2 }, py: 1.5, borderBottom: `1px solid ${designTokens.color.divider}` }}>
      <Typography component="dt" variant="body2" color="text.secondary">{label}</Typography>
      <Typography component="dd" variant="body2" sx={{ m: 0, fontWeight: 650, overflowWrap: 'anywhere' }}>{value}</Typography>
    </Box>
  );
}

function ReviewItem({ label, value, meta, onEdit }: { label: string; value: string; meta: string; onEdit: () => void }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr auto', sm: '140px minmax(0, 1fr) auto' }, columnGap: 2, rowGap: 0.3, alignItems: 'center', py: 1.5, borderBottom: `1px solid ${designTokens.color.divider}` }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>{value}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{meta}</Typography>
      </Box>
      <CompactEditButton aria-label={`${label} 수정`} onClick={onEdit}>수정</CompactEditButton>
    </Box>
  );
}
